import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  IconButton,
  Alert, 
  AlertTitle,
} from "@mui/material";
import { Theme, useTheme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import BallotIcon from "@mui/icons-material/Ballot";
import { useLocalStorage } from "../../utils/LocalStorageHook";
import { getRequests, flattenRequests } from "../../utils/GetRequest";
import ResponsiveDrawer from "../../components/ClippedResponsiveDrawer";
import requestMissingContexts, { determineConfig } from "../../utils/GetContext";
import TableAbstraction from "../../componentUtils/TableAbstraction"
import {checkForRole} from "../../utils/GetAuth";
import PageInPage from "../../componentUtils/PageInPage";
import StarRate from "../../componentUtils/StarRate";
import RecommendationComponent from "../../components/request/Recommend";
import {
  Lock,
  Brightness1,
 } from '@mui/icons-material';
import VendPrimeRequest from "./VendPrimeRequest";
import { prettyPrintDollars } from "../../utils/Formatter"
import { GetStatusBadge } from "../../components/request/requestUtils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {},
    divWidth: {
      width: "100%",
    },
    paper: {
        position: 'absolute',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid`,
        borderColor: theme.palette.border.main, 
        boxShadow: theme.shadows[4],
        padding: theme.spacing(2, 2, 2),
    },
    iconRed: {
      color: "Crimson",
      fontSize: 13,
    },
    iconYellow: {
      color: "Gold",
      fontSize: 13,
    },
    iconGreen: {
      color: "LimeGreen",
      fontSize: 13,
    },
    iconDisabled: {
      color: "LightGray",
      fontSize: 13,
    },
  })
);
export default function VendPrimeRequestQueue() {
  const classes = useStyles();
  const [vpRequests, SetVpRequests] = useState<any[]>([]);
  const [percentileConsumption, setPercentileConsumption] = useState<any>();
  const [codes, setCodes] = React.useState<any>();
  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");
  const [doneLoading, setDoneLoading] = React.useState<boolean>();
  const [clientId,] = useLocalStorage("SETTINGS.clientId", 100);
  const enabled = checkForRole(`${clientId}`,salesOrg,'vp_access');
  
  const header =  document.querySelector('ResponsiveDrawer')
  const headerAdj = header?header.clientHeight:200;
  const [TABLE_BODY_HEIGHT, setTABLE_BODY_HEIGHT] = React.useState<string>(`${window.innerHeight - headerAdj }px`);
  window.addEventListener('resize', ()=>{
    setTABLE_BODY_HEIGHT(`${window.innerHeight - headerAdj }px`);
  })

  const [muiTable, setMuiTable] = React.useState<any>({rpp:25});

  const [displayPIP, setDisplayPIP] = React.useState<boolean>(false);
  const [displayREC, setDisplayREC] = React.useState<boolean>(false);
  const [curKeys, setCurKeys] = React.useState<{requestNum:number,deliveringPlant:string,customerNum:string,customerName:string}|undefined>();
  const [showReco, setShowReco] = useState<any>({});

  useEffect(() => {
    if (doneLoading) return;
    requestMissingContexts(["SALESOFFICES","PLANTS","vp_req_status"])
      .then((v) => {
        setCodes(v);
      })
      .then((_) => {
        getRequests(salesOrg,{userView:"PENDING"}).then((v) => {
          if(v && v.requests){
            let temp:any[] = v.requests;
            SetVpRequests(flattenRequests(temp));
          }
          if(v && v.PercentileMaterialConsumption){
            setPercentileConsumption(v.PercentileMaterialConsumption)

          }
          setDoneLoading(true);
        });
      });
  }, [vpRequests, muiTable, displayPIP, doneLoading, salesOrg]);

  return (
    <ResponsiveDrawer
      title="Pending Queue"
    >
    <div className={classes.root}>
      {doneLoading && enabled? (
        <TableAbstraction
          settingsKey="VP_RPQ"
          options={{
            draggableColumns: {
              enabled: true,
            },
            rowsPerPageOptions: [25, 50, 100],
            serverSide: false,
            selectableRows: "none",
            rowsPerPage: muiTable.rpp,
            onChangeRowsPerPage: (numberOfRows) => {
              muiTable.rpp = numberOfRows;
              setMuiTable(muiTable);
            },
            onRowClick: (params) =>{ 
              setCurKeys({
                requestNum:params.row.requestNum,
                deliveringPlant:params.row.deliveringPlant,
                customerNum:params.row.customerNum,
                customerName:params.row.name1 + ' [' + params.row.name3 +']'
              });
              setDisplayPIP(true);
            }
          }}
          title="Pending Queue (Approve or Reject)"
          columns={[
            {
              label: "Request Num",
              name: "requestNum",
              options: {
                display: true,
                searchable: true,
                filter: false,
              },
            },
            {
              label: "Sales Office",
              name: "salesOffice",
              options: {
                filter: true,
                customBodyRender: (value: any) => `${value} - ${determineConfig(codes, "SALESOFFICES", value)}` ,
              },
            },
            {
              label: "Delivery Plant",
              name: "plant",
              options: {
                filter: true,
                customBodyRender: (value: any) => `${value} - ${determineConfig(codes, "PLANTS", value)}` ,
              },
            },
            {
              label: "Customer",
              name: "customerNum",
              options: {
                searchable: true,
                filter: false,
              },
            },
            {
              label: "Name",
              name: "customerName1",
              options: {
                searchable: true,
                filter: false,
              },
            },
            {
              label: "Route",
              name: "masterRoute",
              options: {
                searchable: true,
                filter: false,
              },
            },
            {
              label: "Model",
              name: "model",
              options: {
                searchable: true,
                filter: true,
              },
            },
            {
              label: "Units/Day",
              name: "vends",
              options: {
                searchable: true,
                filter: false,
              },
            },
            {
              label: "Net $/Day",
              name: "revenue",
              options: {
                searchable: true,
                filter: false,
              },
            },
            {
              label: "Oppr. $/Day",
              name: "opprPerDay",
              options: {
                searchable: true,
                filter: false,
                customBodyRender: (value: any) => (
                  <div style={{color:(value<0)?'red':''}}>
                    {prettyPrintDollars(value||0)}
                  </div>
                )
              },
            },
            {
              label: "PFR@OOS",
              name: "efr",
              options: {
                searchable: true,
                filter: false,
                customBodyRender: (value: any) => (
                  <div>
                    <Brightness1
                      fontSize="small"
                      className={
                        value
                          ? value <= 50
                            ? classes.iconRed
                            : value <= 70
                            ? classes.iconYellow
                            : classes.iconGreen
                          : classes.iconDisabled
                      }
                    />{" "}
                    {value ? `${value}%` : `???`}
                  </div>
                ),
              },
            },
            {
              label: "Efficiency",
              name: "vends",
              options: {
                searchable: true,
                filter: false,
                customBodyRender: (value:any)=>{
                  let stars = 0.5
                  if(value >= percentileConsumption.percentile_25) stars=1
                  if(value >= percentileConsumption.percentile_50) stars=1.5
                  if(value >= percentileConsumption.percentile_75) stars=2
                  if(value >= percentileConsumption.percentile_90) stars=2.5
                  if(value >= percentileConsumption.percentile_95) stars=3
                  return(<StarRate value={stars} max={3} fontSize="small" />)
              }
              },
            },
            {
              label: "Status",
              name: "requestStatus",
              options: {
                searchable: true,
                filter: true,
                customBodyRender: (value: any) =>  (<GetStatusBadge status={value} codes={codes} />),
              },
            },
            {
              label: "Last Change",
              name: "maxDt",
              options: {
                searchable: true,
                filter: false,
              },
            },
            {
              label: "Requested By",
              name: "requestedBy",
              options: {
                searchable: true,
                filter: true,
              },
            },
            {
              label: "Reco",
              name: "guid",
              options: {
                searchable: true,
                filter: true,
                customBodyRender: (value:any)=>{
                  let customerInfo = vpRequests.find(
                    (v: any) => v.guid === (value as any)
                  );
                  if (customerInfo.origRequestNum != null) {
                      return( <IconButton
                        onClick={() => {
                        //  console.log(customerInfo)
                          setShowReco(customerInfo);
                          setDisplayREC(true);
                        }}
                      >
                        
                        <BallotIcon fontSize="small" />
                      </IconButton>
                      )
                  } else {
                       return <div />;
                  }
              }
              },
            },
          ]}
          data={vpRequests}
        />
      ) : ( enabled ? (
        <div className={classes.centerDiv}>
          <CircularProgress color="primary" />
        </div>) : (
          <Alert severity="error">
            <AlertTitle><Lock/> Access Error</AlertTitle>   
            You do not currently have access to <strong>Vend Prime</strong> activities for the <strong>{salesOrg}</strong> bottler. 
          </Alert>
        )
      )}
      <PageInPage onClose={()=>{setDisplayPIP(false)}} title={`${curKeys?curKeys.deliveringPlant:''} (${curKeys?curKeys.customerNum:''}) ${curKeys?curKeys.customerName:''}`} isOpen={displayPIP} content={(<VendPrimeRequest requestNum={curKeys?curKeys.requestNum:0} onClose={()=>{setDisplayPIP(false); SetVpRequests(vpRequests.filter((r)=> r.requestNum !== (curKeys?curKeys.requestNum:0))); }} />)} />
      <PageInPage
    onClose={() => {
      setDisplayREC(false);
    }}
    title={`Reconfiguration for ${showReco.deliveringPlant}: ${showReco.customerNum} ${showReco.name1} [${showReco.name3}]`}
    isOpen={displayREC}
    content={
      <RecommendationComponent
        fullpage={false}
        customer={showReco.customerNum}
      />
    }
  />
    </div>
    </ResponsiveDrawer>
    
  );
}